/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationsRevokeInvite } from "../funcs/organizationsRevokeInvite.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeInviteRequest,
  RevokeInviteSecurity,
} from "../models/operations/revokeinvite.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeInviteMutationVariables = {
  request: RevokeInviteRequest;
  security?: RevokeInviteSecurity | undefined;
  options?: RequestOptions;
};

export type RevokeInviteMutationData = void;

export type RevokeInviteMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revokeInvite organizations
 *
 * @remarks
 * Revoke a pending WorkOS invitation.
 */
export function useRevokeInviteMutation(
  options?: MutationHookOptions<
    RevokeInviteMutationData,
    RevokeInviteMutationError,
    RevokeInviteMutationVariables
  >,
): UseMutationResult<
  RevokeInviteMutationData,
  RevokeInviteMutationError,
  RevokeInviteMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeInviteMutation(client, options),
    ...options,
  });
}

export function mutationKeyRevokeInvite(): MutationKey {
  return ["@gram/client", "organizations", "revokeInvite"];
}

export function buildRevokeInviteMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RevokeInviteMutationVariables,
  ) => Promise<RevokeInviteMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeInvite(),
    mutationFn: function revokeInviteMutationFn({
      request,
      security,
      options,
    }): Promise<RevokeInviteMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationsRevokeInvite(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
